import { DecodedInvoiceSummary } from "./invoiceSummary";

export type CheckLevel = "ok" | "warn" | "fail" | "info";

export interface InvoiceCheck {
  level: CheckLevel;
  title: string;
  detail?: string;
}

/** Under this many seconds left, the expiry turns amber ("pay now"). */
export const EXPIRES_SOON_SECONDS = 120;

export type ExpiryState =
  | { kind: "unknown" }
  | { kind: "valid"; secondsLeft: number; soon: boolean }
  | { kind: "expired"; secondsAgo: number };

export const expiryState = (
  invoice: DecodedInvoiceSummary,
  nowMs: number
): ExpiryState => {
  if (!invoice.timestamp || invoice.expiry === null) return { kind: "unknown" };
  const expiresAtMs = (invoice.timestamp + invoice.expiry) * 1000;
  const diff = Math.round((expiresAtMs - nowMs) / 1000);
  if (diff <= 0) return { kind: "expired", secondsAgo: -diff };
  return { kind: "valid", secondsLeft: diff, soon: diff < EXPIRES_SOON_SECONDS };
};

/** "45 s", "14 min 32 s", "3 h 5 min", "2 days 4 h" */
export const formatDuration = (totalSeconds: number): string => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  if (days > 0) return `${days} day${days > 1 ? "s" : ""}${hours ? ` ${hours} h` : ""}`;
  if (hours > 0) return `${hours} h${minutes ? ` ${minutes} min` : ""}`;
  if (minutes > 0) return seconds ? `${minutes} min ${seconds} s` : `${minutes} min`;
  return `${seconds} s`;
};

const networkName = (invoice: DecodedInvoiceSummary): string =>
  (invoice.network || invoice.networkLetters || "another network").replace(/^Bitcoin /, "");

/**
 * Plain-language checks on a decoded invoice. Only uses what's in the invoice plus our
 * copy of the public Lightning graph; it never contacts the payee or probes a route, so
 * "ok" here means "nothing wrong in the invoice", not "a payment will succeed".
 * Expiry is shown separately (it ticks), so it isn't in this list.
 */
export const invoiceChecks = (
  invoice: DecodedInvoiceSummary,
  nowMs: number
): InvoiceCheck[] => {
  const checks: InvoiceCheck[] = [];

  // Network
  if (invoice.networkLetters === "bc") {
    checks.push({ level: "ok", title: "Bitcoin mainnet invoice" });
  } else {
    checks.push({
      level: "fail",
      title: `This invoice is for ${networkName(invoice)}, not mainnet`,
      detail: `A normal (mainnet) Lightning wallet can't pay it. ${networkName(
        invoice
      )} coins are for testing and have no value.`,
    });
  }

  // Amount
  if (invoice.amountInSatoshis === null) {
    checks.push({
      level: "warn",
      title: "No amount in the invoice",
      detail:
        "The payer chooses how much to send, so check the amount with the recipient first. Some wallets and exchanges can't pay invoices without an amount.",
    });
  } else {
    checks.push({
      level: "ok",
      title: `Amount: ${invoice.amountInSatoshis.toLocaleString()} sats`,
    });
  }

  // Creation time in the future: the recipient's clock is off
  if (invoice.timestamp && invoice.timestamp * 1000 > nowMs + 10 * 60 * 1000) {
    checks.push({
      level: "warn",
      title: "Created in the future",
      detail:
        "The invoice's creation time is ahead of your clock. The recipient's device clock (or yours) is probably wrong, and some wallets may refuse it.",
    });
  }

  // Very short lifetime (as issued, not what's left)
  if (invoice.expiry !== null && invoice.expiry > 0 && invoice.expiry < 120) {
    checks.push({
      level: "warn",
      title: `Very short expiry: ${formatDuration(invoice.expiry)} from creation`,
      detail: "It has to be paid almost immediately; ask for a new one if it runs out.",
    });
  }

  // Payment secret: required by BOLT 11 since 2021, most wallets refuse invoices without it
  if (!invoice.paymentSecret) {
    checks.push({
      level: "warn",
      title: "No payment secret",
      detail:
        "Modern Lightning wallets require one, so many of them will refuse to pay this invoice.",
    });
  }

  // Description
  if (!invoice.description && invoice.descriptionHash) {
    checks.push({
      level: "info",
      title: "Description stored as a hash",
      detail:
        "Usual for Lightning Address and LNURL invoices: the invoice commits to a description kept by the service that made it, so the text isn't in the invoice itself.",
    });
  } else if (!invoice.description) {
    checks.push({
      level: "info",
      title: "No description",
      detail: "Nothing tells the payer what this payment is for; that's allowed, but check with the recipient.",
    });
  }

  // Destination vs our copy of the public graph
  const hints = invoice.routeHints.length;
  if (invoice.destinationInGraph === true) {
    checks.push({
      level: "ok",
      title: "Payee is a public Lightning node",
      detail: "It's in the public Lightning graph, so wallets can find routes to it.",
    });
  } else if (invoice.destinationInGraph === false && hints > 0) {
    checks.push({
      level: "info",
      title: `Private payee node, reached through ${hints} route hint${hints > 1 ? "s" : ""}`,
      detail:
        "Normal for mobile and custodial wallets: the node isn't announced, and the route hints tell the payer's wallet which public nodes it's connected to.",
    });
  } else if (invoice.destinationInGraph === false) {
    checks.push({
      level: "warn",
      title: "Payee not in the public graph, and no route hints",
      detail:
        "We can't see this node in the public Lightning graph and the invoice doesn't say how to reach it, so most wallets won't find a route. Wallets with a direct channel to it (often the same provider) can still pay. A node that came online in the last few hours may not have reached our copy of the graph yet.",
    });
  }

  return checks;
};
